import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { createEffect, Effect, Actions, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import {
  AccountsActionTypes,
  LoadAccountCategoriesSuccess,
  LoadAccountCategoriesFailure,
  LoadAccountsSuccess,
  LoadAccountsFailure,
  CreateAccountSuccess,
  CreateAccountFailure,
  AccountBootstrapPending,
  AccountBootstrapDone,
  AccountBootstrapCheckFailed,
  CreateAccount,
  InviteAccountAdmin,
  InviteAccountAdminFailure,
  InviteAccountAdminSuccess,
  LoadAccounts,
  DeleteAccount,
  DeleteAccountSuccess,
  DeleteAccountFailure,
  PrepareCreateAccount,
  PrepareCreateAccountFailure,
  LoadAccountMigrations,
  LoadAccountMigrationsSuccess,
  LoadAccountMigrationsFailure,
  UpdateLicensePackageSuccess,
  UpdateLicensePackageFailure,
  UpdateLicensePackage,
  GetProAccountsAction,
  GetProAccountsSuccessAction,
  GetProAccountsFailureAction,
  AccountBootstrapEmailEventSuccessAction,
  AccountBootstrapEmailEventFailureAction,
  CreateCrmKeyspaceAction,
  CreateCrmKeyspaceSuccessAction,
  CreateCrmKeyspaceFailureAction,
  CreateCrmKeyspaceTimeoutFailure,
  AccountActiveAction,
  AccountActiveSuccessAction,
  AccountActiveFailureAction,
  AccountLockAction,
  AccountLockSuccessAction,
  AccountLockFailureAction,
  RebootstrapLicensesAction,
  RebootstrapLicensesSuccessAction,
  RebootstrapLicensesFailureAction,
  RebootstrapLicensesTimeoutFailureAction,
  EmailQuota,
  SmsCredits,
} from './accounts.actions';
import { of } from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  exhaustMap,
  delay,
  retry,
  mergeMap,
  tap,
} from 'rxjs/operators';
import { AccountService } from '@account-users/account/services/account.service';
import { AccountState } from '@account-users/account/models/IAccount';
import { ProAccountsService } from '@account-users/account/services/pro-accounts.service';
import { SmsCreditsService } from '@account-users/account/services/sms-credits.service';
import { AuthService } from '@account-users/auth/auth.service';
import { UserRoles } from '@account-users/account/models/UserRoles';
import { AccountPackageResponse } from '@account-users/account/models/AccountSettingsPayload';
import { FeatureToggleStateService } from '@account-users/account/state/feature-toggles/feature-toggle-state.service';

@Injectable()
export class AccountsEffects {
  @Effect()
  getProAccounts = this.actions$.pipe(
    ofType(AccountsActionTypes.GetProAccounts),
    switchMap(({ payload }: GetProAccountsAction) =>
      this.proAccountService.getProAccounts(payload).pipe(
        map((proAccounts) => new GetProAccountsSuccessAction(proAccounts)),
        catchError(() => {
          this.toastr.error('Error while fetching pro accounts');
          return [new GetProAccountsFailureAction()];
        }),
      ),
    ),
  );

  @Effect()
  loadAccountCategories = this.actions$.pipe(
    ofType(AccountsActionTypes.LoadAccountCategories),
    switchMap(() => {
      return this.accountService.fetchAccountCategories().pipe(
        map((response) => new LoadAccountCategoriesSuccess(response)),
        catchError((error) => {
          this.toastr.error(
            'Could not fetch account categories',
            'Request failed',
          );
          return [new LoadAccountCategoriesFailure()];
        }),
      );
    }),
  );

  @Effect()
  loadAccounts = this.actions$.pipe(
    ofType(AccountsActionTypes.LoadAccounts),
    mergeMap(({ payload }: LoadAccounts) => {
      return this.accountService.fetchAccounts(payload).pipe(
        map((response) => new LoadAccountsSuccess(response)),
        catchError((error) => {
          this.toastr.error('Could not fetch accounts', 'Request failed');
          return [new LoadAccountsFailure()];
        }),
      );
    }),
  );

  @Effect()
  loadAccountMigrations = this.actions$.pipe(
    ofType(AccountsActionTypes.LoadAccountMigrations),
    switchMap(({ payload: { accountId } }: LoadAccountMigrations) => {
      return this.accountService.fetchAccountMigrations(accountId).pipe(
        switchMap((migrations) => {
          return [new LoadAccountMigrationsSuccess({ migrations })];
        }),
        catchError(() => {
          this.toastr.error('Could not fetch migrations', 'Request failed');
          return [new LoadAccountMigrationsFailure()];
        }),
      );
    }),
  );

  @Effect()
  prepareCreateAccount = this.actions$.pipe(
    ofType(AccountsActionTypes.PrepareCreateAccount),
    switchMap(({ payload }: PrepareCreateAccount) => {
      return this.auth.isTokenAboutToExpire()
        ? this.auth.renewTokens().pipe(
            map((authResult) => {
              this.auth.setSession(authResult);
              return new CreateAccount(payload);
            }),
            catchError((err) => {
              this.toastr.error(
                'Could not create the account because of failed authentication. Please reload the page and try again.',
                'Create account failed',
                { disableTimeOut: true, closeButton: true },
              );

              return [new PrepareCreateAccountFailure()];
            }),
          )
        : [new CreateAccount(payload)];
    }),
  );

  @Effect()
  createAccount = this.actions$.pipe(
    ofType(AccountsActionTypes.CreateAccount),
    switchMap(({ payload }: CreateAccount) => {
      return this.accountService.createAccount(payload).pipe(
        switchMap((response) => [
          new CreateAccountSuccess(response),
          new AccountBootstrapPending({
            accountId: response.id,
            adminEmail: payload.adminEmail,
            emailQuota: payload.emailQuota,
            smsCredits: payload.smsCredits,
            orderNumber: payload.orderNumber,
          }),
        ]),
        catchError((error) => {
          const message =
            error.status === 409
              ? 'Account name already exist'
              : error?.error?.message ?? 'Could not create the account';
          this.toastr.error(message, 'Request failed');
          return [new CreateAccountFailure()];
        }),
      );
    }),
  );

  @Effect()
  checkAccountStatus = this.actions$.pipe(
    ofType(AccountsActionTypes.AccountBootstrapPending),
    delay(1000),
    exhaustMap(({ payload }: AccountBootstrapPending) => {
      return this.accountService.getAccountDetails(payload.accountId).pipe(
        switchMap((account) => {
          if (account.state === AccountState.Pending) {
            return [new AccountBootstrapPending(payload)];
          }

          return [
            new AccountBootstrapDone(account),
            new InviteAccountAdmin({
              accountId: account.id,
              email: payload.adminEmail,
              toolsPermissions: [],
              roles: [UserRoles.AccountOwner, UserRoles.User],
            }),
            EmailQuota.request({
              accountId: account.id,
              emailQuota: payload.emailQuota,
              orderId: payload.orderNumber,
            }),
            SmsCredits.request({
              accountId: account.id,
              smsCredits: payload.smsCredits,
              orderId: payload.orderNumber,
            }),
          ];
        }),
        retry(2),
        catchError((error) => {
          return [new AccountBootstrapCheckFailed()];
        }),
      );
    }),
  );

  @Effect()
  inviteAccountAdmin = this.actions$.pipe(
    ofType(AccountsActionTypes.InviteAccountAdmin),
    switchMap(({ payload }: InviteAccountAdmin) => {
      return this.accountService.inviteUser(payload).pipe(
        map(() => new InviteAccountAdminSuccess()),
        catchError((error) => {
          return [new InviteAccountAdminFailure()];
        }),
      );
    }),
  );

  addEmailQuota$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EmailQuota.request),
      exhaustMap(({ accountId, emailQuota, orderId }) =>
        this.accountService
          .addEmailQuota(accountId, orderId, {
            emailQuota,
            emailOneTimeQuota: 0,
          })
          .pipe(
            map(() => EmailQuota.success()),
            catchError((err) => {
              this.toastr.error(
                err?.error?.message ?? 'Failed to update an email quota',
              );
              return of(EmailQuota.failure());
            }),
          ),
      ),
    ),
  );

  addSmsCredits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SmsCredits.request),
      exhaustMap(({ accountId, smsCredits, orderId }) =>
        this.smsCreditsService
          .patchSmsCredits({ accountId, addCredits: smsCredits, orderId })
          .pipe(
            map(() => SmsCredits.success()),
            catchError((err) => {
              this.toastr.error(
                err?.error?.message ?? 'Failed to update SMS Credits',
              );
              return of(SmsCredits.failure);
            }),
          ),
      ),
    ),
  );

  @Effect()
  deleteAccount = this.actions$.pipe(
    ofType(AccountsActionTypes.DeleteAccount),
    switchMap(({ payload }: DeleteAccount) => {
      return this.accountService.deleteAccountAsync(payload).pipe(
        map(() => new DeleteAccountSuccess(payload)),
        catchError((error) => {
          this.toastr.error('Could not delete account', 'Request failed');
          return [new DeleteAccountFailure()];
        }),
      );
    }),
  );

  @Effect()
  updateAccountLicensePackage$ = this.actions$.pipe(
    ofType(AccountsActionTypes.UpdateLicensePackage),
    switchMap(
      ({
        payload: { accountId, orderId, licensePackage },
      }: UpdateLicensePackage) => {
        return this.accountService
          .updateAccountLicensePackage(accountId, orderId, licensePackage)
          .pipe(
            map((response: AccountPackageResponse[]) => {
              this.toastr.success('Account package successfully updated');
              return new UpdateLicensePackageSuccess({
                accountId,
                licensePackage,
                licenses: response,
              });
            }),
            catchError((error) => {
              this.toastr.error(
                'Could not update Account license package',
                'Request failed',
              );
              return [new UpdateLicensePackageFailure()];
            }),
          );
      },
    ),
  );

  updateLicensePackageSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountsActionTypes.UpdateLicensePackageSuccess),
        switchMap(({ payload: { accountId, licenses } }) => {
          this.featureToggleStateService.loadLicenses(accountId);
          return of(true).pipe(
            delay(500),
            tap(() =>
              this.featureToggleStateService.updateLicense(accountId, licenses),
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  @Effect()
  AccountBootstrapEmailEvent$ = this.actions$.pipe(
    ofType(AccountsActionTypes.AccountBootstrapEmailEvent),
    switchMap(({ payload: { accountId, eventDiscriminators } }) =>
      this.accountService
        .bootstrapAccountByEvents(accountId, eventDiscriminators)
        .pipe(
          map(() => {
            this.toastr.success('Event has successfully been bootstrapped');
            return new AccountBootstrapEmailEventSuccessAction();
          }),
          catchError(() => {
            this.toastr.error('Could not bootstrap event');
            return [new AccountBootstrapEmailEventFailureAction()];
          }),
        ),
    ),
  );

  @Effect()
  CreateCrmKeyspace$ = this.actions$.pipe(
    ofType(AccountsActionTypes.CreateCrmKeyspace),
    exhaustMap(({ payload }: CreateCrmKeyspaceAction) =>
      this.accountService.createCrmKeyspace(payload).pipe(
        map(() => {
          this.toastr.success('CRM-ID keyspace has been successfully created');
          return new CreateCrmKeyspaceSuccessAction();
        }),
        catchError((err: HttpErrorResponse) => {
          const msg =
            err.status === 504
              ? 'CRM-ID keyspace setup takes longer than expected. It should be completed in a few minutes'
              : 'Could not add CRM-ID keyspace';
          this.toastr.error(msg);
          return err.status === 504
            ? [new CreateCrmKeyspaceTimeoutFailure()]
            : [new CreateCrmKeyspaceFailureAction()];
        }),
      ),
    ),
  );

  @Effect()
  rebootstrapLicenses$ = this.actions$.pipe(
    ofType(AccountsActionTypes.RebootstrapLicenses),
    exhaustMap(({ payload }: RebootstrapLicensesAction) =>
      this.accountService.rebootstrapLicenses(payload).pipe(
        map(() => {
          this.toastr.success('License re-bootstrap has been initialized');
          return new RebootstrapLicensesSuccessAction();
        }),
        catchError((err: HttpErrorResponse) => {
          const msg =
            err.status === 504
              ? 'Rebootstrap licenses takes longer than expected. It should be completed in a few minutes'
              : 'Could not rebootstrap licenses';
          this.toastr.error(msg);
          return err.status === 504
            ? [new RebootstrapLicensesTimeoutFailureAction()]
            : [new RebootstrapLicensesFailureAction()];
        }),
      ),
    ),
  );

  @Effect()
  accountActive$ = this.actions$.pipe(
    ofType(AccountsActionTypes.AccountActive),
    exhaustMap(({ payload }: AccountActiveAction) =>
      this.accountService.accountActivate(payload).pipe(
        map(() => new AccountActiveSuccessAction()),
        catchError(() => {
          this.toastr.error(`Account ${payload} activation has failed`);
          return [new AccountActiveFailureAction()];
        }),
      ),
    ),
  );

  @Effect()
  accountLock$ = this.actions$.pipe(
    ofType(AccountsActionTypes.AccountLock),
    exhaustMap(({ payload }: AccountLockAction) =>
      this.accountService.accountLock(payload).pipe(
        map(() => new AccountLockSuccessAction()),
        catchError(() => {
          this.toastr.error(`Account ${payload} cannot be locked`);
          return [new AccountLockFailureAction()];
        }),
      ),
    ),
  );

  constructor(
    private readonly toastr: ToastrService,
    private readonly actions$: Actions,
    private readonly accountService: AccountService,
    private readonly smsCreditsService: SmsCreditsService,
    private readonly proAccountService: ProAccountsService,
    private readonly featureToggleStateService: FeatureToggleStateService,
    private readonly auth: AuthService,
  ) {}
}
